import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DocumentNode,
  type ErrorLike,
  NetworkStatus,
} from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Timeline } from '@mui/lab';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import type {
  ActivityFiltersActivityFragment,
  ActivityItemFragment,
  Activity_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import type { ActivityEmailFormProps } from '@modules/_shared/components/email-form/types';
import {
  ActivityProvider,
  useActivityContext,
} from '@modules/_shared/components/feed/ActivityProvider';
import type { ParentTypeOption } from '@modules/_shared/components/feed/types';
import { ActivityFilters } from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivityFilters';
import { ActivitySortOrder } from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivitySortSelect';
import { ActivityItem } from '@modules/_shared/components/feed-v2/components/ActivityItem';
import { type DateRange } from '@modules/_shared/components/feed-v2/components/DateRangeSelect';
import { EmailFormWrapper } from '@modules/_shared/components/feed-v2/components/EmailFormWrapper';
import { QuickActions } from '@modules/_shared/components/feed-v2/components/QuickActions';
import { FEED_HIDDEN_ACTIVITY_TYPES } from '@modules/_shared/components/feed-v2/constants/activityConfig';
import { timelineStyles } from '@modules/_shared/components/feed-v2/constants/styles';
import { DefaultActivityEmailForm } from '@modules/_shared/components/feed-v2/email/DefaultActivityEmailForm';
import {
  buildSearchableActivities,
  filterActivities,
  groupActivities,
  sortByCreatedDate,
  sortStrategies,
  useTranslateActivityType,
} from '@modules/_shared/components/feed-v2/utils/activityHelpers';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useLocale } from '@shared/hooks/locale';

type FeedActivity = ActivityItemFragment & ActivityFiltersActivityFragment;

const DEFAULT_PAGE_SIZE = 50;

// Stable reference for the empty state so the downstream search/filter/group
// useMemos don't recompute every render during the initial loading window
// (a fresh `[]` would change identity each time `data` is undefined).
const EMPTY_ACTIVITIES: FeedActivity[] = [];

// Render the Load more button in Virtuoso's Footer slot so it sits flush
// under the last item (no virtualizer reserve gap). The component reference
// must be stable — Virtuoso compares `components` by identity and remounts on
// a fresh one, which would drop button focus mid-click. So the dynamic button
// flows in through Virtuoso's `context` prop instead of being closed over:
// context changes re-render the footer in place rather than remounting it.
type FeedFooterContext = { loadMoreButton: React.ReactNode };
const FeedFooter = ({ context }: { context?: FeedFooterContext }) => (
  <>{context?.loadMoreButton ?? null}</>
);
const VIRTUOSO_COMPONENTS = { Footer: FeedFooter };

type ActivityFeedQueryResult = {
  activities: FeedActivity[];
};

type ActivityFeedProps = {
  // Null when the feed has no single parent (e.g. dashboard). Quick actions
  // and the email form hide in that case — they need a parent to attach to.
  parentType?: ParentTypeOption | null;
  parentId?: string | null;
  primaryLeadId?: string;
  disabled?: boolean;
  fallbackPhoneNumber?: string | null;
  scrollParent: HTMLElement | null;
  query: DocumentNode;
  variables: Record<string, unknown>;
  // Opt in to offset pagination + a "Load more" button. When false (default),
  // the feed loads in one shot exactly as before — no limit/offset injected,
  // no button — so feeds that haven't opted in (e.g. the dashboard) are
  // unchanged.
  paginate?: boolean;
  // How many activities to fetch per page when `paginate`. Used as both the
  // initial $limit and the page size for "Load more". Defaults to 50.
  pageSize?: number;
  showFilters?: boolean;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  overdue?: boolean;
  onOverdueChange?: (overdue: boolean) => void;
  header?: React.ReactNode;
  EmailFormComponent?: React.ComponentType<ActivityEmailFormProps>;
};

export const ActivityFeed = ({
  parentType,
  parentId,
  primaryLeadId,
  disabled,
  fallbackPhoneNumber,
  scrollParent,
  query,
  variables,
  paginate = false,
  pageSize = DEFAULT_PAGE_SIZE,
  showFilters = true,
  dateRange,
  onDateRangeChange,
  overdue,
  onOverdueChange,
  header,
  EmailFormComponent,
}: ActivityFeedProps) => {
  // Latches when a fetchMore returns no rows. Reset whenever queryVariables
  // changes (filter / date-range switches start a fresh paginated cursor) and
  // when refetch is called (subscribeFeedRefresh after creating a note, etc.).
  const [reachedEnd, setReachedEnd] = useState(false);

  // Stringify once so the useMemo dep array is a primitive — keeps
  // react-hooks/exhaustive-deps happy without an eslint-disable.
  const variablesKey = JSON.stringify(variables);

  const queryVariables = useMemo(
    () =>
      paginate
        ? { ...variables, limit: pageSize, offset: 0 }
        : { ...variables },
    // variablesKey captures the contents of `variables`; the lint rule can't
    // see through that, so we list it instead of `variables` itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variablesKey, pageSize, paginate],
  );

  useEffect(() => {
    setReachedEnd(false);
  }, [queryVariables]);

  const { data, networkStatus, refetch, fetchMore, error } =
    useQuery<ActivityFeedQueryResult>(query, {
      variables: queryVariables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });

  // Bumped on every refetch / data reset so an in-flight fetchMore can tell
  // its result is stale on completion. Without it, a refetch (subscribeFeedRefresh
  // or a sibling refetchQueries) that lands mid-fetchMore and shrinks the
  // cache would let the now-empty fetchMore latch reachedEnd against the
  // fresh page — wrongly hiding Load more after a new activity was added.
  const refetchGenRef = useRef(0);

  const handleRefetch = useCallback(() => {
    refetchGenRef.current += 1;
    setReachedEnd(false);
    refetch();
  }, [refetch]);

  // Pagination below counts the rows the server returned; only rendering
  // drops the hidden onboarding types.
  const activities = data?.activities ?? EMPTY_ACTIVITIES;
  const visibleActivities = useMemo(
    () =>
      activities.some(activity =>
        FEED_HIDDEN_ACTIVITY_TYPES.has(activity.activity_type),
      )
        ? activities.filter(
            activity => !FEED_HIDDEN_ACTIVITY_TYPES.has(activity.activity_type),
          )
        : activities,
    [activities],
  );

  // Any refetch re-opens pagination, so clear the latch and bump the
  // generation when one starts. This covers handleRefetch and — crucially —
  // a sibling `refetchQueries: [LEAD_FEED]` (e.g. assignLead in
  // LeadAgentSelectCard) that doesn't go through handleRefetch. Keying off the
  // network status rather than a row-count drop also handles the case where
  // the refetched first page has the same length as the latched tail (50
  // loaded + latched, another actor adds #51, refetch returns 50 — no shrink,
  // but #51 is now reachable).
  useEffect(() => {
    if (networkStatus === NetworkStatus.refetch) {
      refetchGenRef.current += 1;
      setReachedEnd(false);
    }
  }, [networkStatus]);

  // While a variable-change refetch is in flight, `data` still holds the old
  // result. If the old length is a multiple of pageSize, the button would
  // briefly show and clicking would issue a fetchMore at the wrong offset.
  const isRefetching =
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch;

  // Heuristic: a full page implies more rows likely exist. `reachedEnd`
  // latches when a fetchMore comes back empty so the button hides instead of
  // looping no-op fetches at the exact tail.
  const hasMore =
    paginate &&
    !reachedEnd &&
    !isRefetching &&
    activities.length > 0 &&
    activities.length % pageSize === 0;

  // Always reflects current variables so the post-await check can tell whether
  // the user changed a filter / date range while a fetchMore was in flight.
  const queryVariablesRef = useRef(queryVariables);
  queryVariablesRef.current = queryVariables;

  const handleLoadMore = useCallback(async () => {
    const requested = queryVariablesRef.current;
    const stampedGen = refetchGenRef.current;
    const expectedLength = activities.length;
    let result;
    try {
      result = await fetchMore({
        variables: { offset: expectedLength },
        updateQuery: (prev, { fetchMoreResult }) => {
          // Guard against an interleaving refetch: a sibling
          // `refetchQueries: [LEAD_FEED]` (e.g. assignLead) can reset the
          // cache to page 1 while this fetchMore is in flight. Appending our
          // offset=expectedLength page onto a now-shorter list would produce
          // a non-contiguous set, so only merge if the cache is still the
          // length we paginated from.
          if (
            !fetchMoreResult?.activities?.length ||
            prev.activities.length !== expectedLength
          ) {
            return prev;
          }
          return {
            ...prev,
            activities: [...prev.activities, ...fetchMoreResult.activities],
          };
        },
      });
    } catch {
      // A failed fetchMore (network / GraphQL error) leaves the latch alone so
      // the button stays clickable for a retry; Apollo resets isLoadingMore
      // via networkStatus. Swallow here to avoid an unhandled rejection.
      return;
    }
    // Only latch if neither the variables nor a refetch happened since we
    // fired. A filter change mid-flight would set reachedEnd against the new
    // query (whose own effect already cleared it); a concurrent refetch
    // (subscribeFeedRefresh / sibling refetchQueries) that shrank the cache
    // means the empty result is against stale state. setReachedEnd also must
    // not live inside updateQuery — Apollo may invoke that pure cache-merge
    // fn more than once per fetch.
    if (
      queryVariablesRef.current === requested &&
      refetchGenRef.current === stampedGen &&
      !result.data?.activities?.length
    ) {
      setReachedEnd(true);
    }
  }, [fetchMore, activities.length]);

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading =
    !data &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables);

  const location = useLocation();
  const focusActivityId =
    (location.state as { focusActivity?: string } | null)?.focusActivity ??
    null;

  return (
    <ActivityProvider
      parentType={parentType ?? null}
      parentId={parentId ?? null}
      disabled={disabled}
      fallbackPhoneNumber={fallbackPhoneNumber}
      EmailFormComponent={EmailFormComponent ?? DefaultActivityEmailForm}
    >
      <ActivityFeedContent
        activities={visibleActivities}
        loading={isInitialLoading}
        queryError={error ?? null}
        primaryLeadId={primaryLeadId}
        parentType={parentType ?? null}
        parentId={parentId ?? null}
        scrollParent={scrollParent}
        onRefetch={handleRefetch}
        focusActivityId={focusActivityId}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
        showFilters={showFilters}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        overdue={overdue}
        onOverdueChange={onOverdueChange}
        header={header}
        disabled={disabled}
      />
    </ActivityProvider>
  );
};

type ActivityFeedContentProps = {
  activities: FeedActivity[];
  loading: boolean;
  queryError: ErrorLike | null;
  primaryLeadId?: string;
  parentType: ParentTypeOption | null;
  parentId: string | null;
  scrollParent: HTMLElement | null;
  onRefetch: () => void;
  focusActivityId: string | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  showFilters: boolean;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  overdue?: boolean;
  onOverdueChange?: (overdue: boolean) => void;
  disabled?: boolean;
  header?: React.ReactNode;
};

const ActivityFeedContent = ({
  activities,
  loading,
  queryError,
  primaryLeadId,
  parentType,
  parentId,
  scrollParent,
  onRefetch,
  focusActivityId,
  hasMore,
  isLoadingMore,
  onLoadMore,
  showFilters,
  dateRange,
  onDateRangeChange,
  overdue,
  onOverdueChange,
  disabled,
  header,
}: ActivityFeedContentProps) => {
  const { t } = useLocale();
  const { reportError } = useActivityContext();
  const [sortOrder, setSortOrder] = useState(ActivitySortOrder.PendingFirst);
  const [selectedTypes, setSelectedTypes] = useState<
    Activity_Types_Enum_Enum[]
  >([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    [],
  );
  const [leadFilter, setLeadFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const translateActivityType = useTranslateActivityType();

  useEffect(() => {
    if (queryError) {
      reportError(queryError);
    }
  }, [queryError, reportError]);

  // Subscribe to feed refresh events
  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      onRefetch();
    });
    return unsubscribe;
  }, [onRefetch]);

  // Pre-compute searchable text
  const activitiesWithSearchText = useMemo(
    () => buildSearchableActivities(activities, translateActivityType),
    [activities, translateActivityType],
  );

  const filteredActivities = useMemo(
    () =>
      filterActivities(
        activitiesWithSearchText,
        { selectedTypes, selectedParticipants, leadFilter, searchTerm },
        primaryLeadId,
      ),
    [
      activitiesWithSearchText,
      selectedTypes,
      selectedParticipants,
      leadFilter,
      searchTerm,
      primaryLeadId,
    ],
  );

  // Memoized so the Virtuoso footer only re-renders on a real change, not on
  // every parent render (context is compared by the element/object identity).
  const loadMoreButton = useMemo(
    () =>
      hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            loading={isLoadingMore}
            loadingPosition="start"
          >
            {t('Load more')}
          </Button>
        </Box>
      ) : null,
    [hasMore, isLoadingMore, onLoadMore, t],
  );
  const footerContext = useMemo<FeedFooterContext>(
    () => ({ loadMoreButton }),
    [loadMoreButton],
  );

  const groupedActivities = useMemo(() => {
    const pinnedActivities = filteredActivities.filter(a => a.pinned);
    const unpinnedActivities = filteredActivities.filter(a => !a.pinned);

    const sortedPinned = [...pinnedActivities].sort(sortByCreatedDate);
    const sortedUnpinned = [...unpinnedActivities].sort(
      sortStrategies[sortOrder] ?? sortByCreatedDate,
    );

    return [
      ...groupActivities(sortedPinned),
      ...groupActivities(sortedUnpinned),
    ];
  }, [filteredActivities, sortOrder]);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const hasScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusActivityId) {
      return;
    }
    if (hasScrolledRef.current === focusActivityId) {
      return;
    }
    if (groupedActivities.length === 0 || !virtuosoRef.current) {
      return;
    }
    const idx = groupedActivities.findIndex(g =>
      g.activities.some(a => a.id === focusActivityId),
    );
    if (idx < 0) {
      return;
    }
    hasScrolledRef.current = focusActivityId;
    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({
        index: idx,
        align: 'center',
        behavior: 'auto',
      });
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          align: 'center',
          behavior: 'smooth',
        });
      }, 150);
    });
  }, [focusActivityId, groupedActivities, scrollParent]);

  return (
    <>
      <Box
        sx={{
          overflow: 'hidden',
          flexShrink: 0,
          width: '100%',
          maxWidth: '100%',
          px: { xs: 2, md: 0 },
        }}
      >
        <Container
          maxWidth="md"
          disableGutters
          sx={{ overflow: 'hidden', maxWidth: '100%' }}
        >
          <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
            {header}
            {/* Hide when there's no parent to attach a new activity to. */}
            {parentType != null && !disabled && <QuickActions />}

            {showFilters && (
              <ActivityFilters
                activities={activities}
                selectedTypes={selectedTypes}
                onSelectedTypesChange={setSelectedTypes}
                selectedParticipants={selectedParticipants}
                onSelectedParticipantsChange={setSelectedParticipants}
                leadFilter={leadFilter}
                onLeadFilterChange={setLeadFilter}
                onSearchTermChange={setSearchTerm}
                onOrderChange={setSortOrder}
                primaryLeadId={primaryLeadId}
                dateRange={dateRange}
                onDateRangeChange={onDateRangeChange}
                overdue={overdue}
                onOverdueChange={onOverdueChange}
                disabled={loading}
              />
            )}

            {parentType != null && <EmailFormWrapper />}

            {loading && <LoadingSpinner />}
            {!loading && groupedActivities.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('No activities found')}
                </Typography>
                {/* Filters (search, types, participants, lead) operate on the
                    loaded set; a narrow filter on page 1 can hide every
                    visible row while more raw rows exist server-side. Keep
                    Load more reachable so the next page can surface a match. */}
                {loadMoreButton}
              </Box>
            )}
            {!loading && groupedActivities.length > 0 && scrollParent && (
              <Virtuoso
                ref={virtuosoRef}
                data={groupedActivities}
                customScrollParent={scrollParent}
                components={VIRTUOSO_COMPONENTS}
                context={footerContext}
                itemContent={(index, group) => {
                  const activity = group.activities[0];
                  // No primary to anchor against: always show the snippet
                  // when the row has a lead/lot/buyer/enquiry context.
                  const isIndirect =
                    parentType === 'User' || parentType == null
                      ? activity.lead_id != null ||
                        activity.lot_id != null ||
                        activity.buyer_lead_id != null ||
                        activity.enquiry_id != null
                      : parentType === 'Lot'
                      ? // Anchor on the lot itself: a row attached directly to
                        // this lot stays non-indirect even if it also carries
                        // other parent FKs. Everything else (linked lead, buyer
                        // lead, enquiry, or a different lot — all with a null or
                        // different lot_id) is indirect and gets a snippet
                        // pointing back to its real parent.
                        activity.lot_id == null || activity.lot_id !== parentId
                      : primaryLeadId != null &&
                        activity.lead_id != null &&
                        activity.lead_id !== primaryLeadId;

                  const isFocused =
                    focusActivityId != null &&
                    group.activities.some(a => a.id === focusActivityId);

                  return (
                    <Timeline sx={timelineStyles}>
                      <ActivityItem
                        group={group}
                        isFirst={index === 0}
                        isLast={index === groupedActivities.length - 1}
                        isIndirect={isIndirect}
                        isFocused={isFocused}
                        onRefetch={onRefetch}
                      />
                    </Timeline>
                  );
                }}
              />
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};
